import { createApi, fetchBaseQuery, } from '@reduxjs/toolkit/query/react';
import { deleteStoreValue, getStoreValue, setStoreValue } from 'pixel-react';
import { CONFIG } from './baseConfig';
import { triggerGlobalLock } from './globalLock';
import { resolveLockSectionFromRoute } from './lockSections';
export const LOCK_STATUS_CODE = 423;
export const REVERT_LOCK_STATUS_CODE = 409;
export const ALL_PROJECTS_ID = 'All Projects';
const getProjectIdFromStore = () => {
    const currentProject = getStoreValue(CONFIG.AUTH.CURRENT_PROJECT);
    const id = currentProject?.id ?? null;
    return id && id !== ALL_PROJECTS_ID ? id : null;
};
const resolveLockSection = (extraOptions, errorData) => {
    if (extraOptions?.lockSection) {
        return extraOptions.lockSection;
    }
    // The lock has to land on the section the user is looking at, so the route
    // wins over the 423 body (which can name the section being imported into).
    const sectionType = errorData
        ?.sectionType;
    return resolveLockSectionFromRoute() ?? sectionType ?? null;
};
const getLockMessage = (payload) => {
    const message = payload?.message;
    return typeof message === 'string' && message.trim() ? message : undefined;
};
const getCurrentUserId = () => getStoreValue(CONFIG.AUTH.TEST_OPTIMIZE_USER)?.responseObject?.id;
const getNonEmptyString = (value) => typeof value === 'string' && value.trim() ? value : undefined;
// The 423/409 body names the user who started the import under
// `responseObject` ({ userId, userName }). Keep this the only place that
// reads those keys so a backend rename stays a one-line change.
const getLockUser = (payload) => {
    const user = payload?.responseObject;
    return {
        userId: getNonEmptyString(user?.userId),
        userName: getNonEmptyString(user?.userName),
    };
};
// 409 is also a plain conflict elsewhere in the app; only a revert's 409
// names the importing user, like the 423 body does.
const getLockType = (statusCode, payload) => {
    if (statusCode === LOCK_STATUS_CODE) {
        return 'import';
    }
    const { userId, userName } = getLockUser(payload);
    if (statusCode === REVERT_LOCK_STATUS_CODE && (userId || userName)) {
        return 'revert';
    }
    return null;
};
const tryTriggerGlobalLock = (statusCode, extraOptions, payload) => {
    const lockType = getLockType(statusCode, payload);
    if (!lockType || (lockType === 'import' && extraOptions?.skipImportLock)) {
        return;
    }
    const projectId = getProjectIdFromStore();
    const sectionType = resolveLockSection(extraOptions, payload);
    if (projectId && sectionType) {
        // The body names the importing user for everyone, the initiator
        // included. Only pass the name on when it is someone else: its absence
        // is what marks this user as the initiator downstream.
        const { userId, userName } = getLockUser(payload);
        const isOtherUser = !!userId && userId !== getCurrentUserId();
        triggerGlobalLock({
            projectId,
            sectionType,
            message: getLockMessage(payload),
            userName: isOtherUser ? (userName ?? userId) : undefined,
            lockType,
        });
    }
};
/* ---------------------------------------------
 * 🔁 Token Refresh State
 * ------------------------------------------- */
let refreshingToken = null;
let refreshRetryCount = 0;
const MAX_REFRESH_RETRIES = 3;
/* ---------------------------------------------
 * 🧠 Create HOF: baseQueryWithInterceptor factory
 * ------------------------------------------- */
export const createBaseQueryWithInterceptor = (baseUrl) => {
    const baseQuery = fetchBaseQuery({
        baseUrl,
        prepareHeaders: headers => {
            const accessToken = getStoreValue(CONFIG.AUTH.TOKEN_KEY);
            const currentProject = getStoreValue(CONFIG.AUTH.CURRENT_PROJECT);
            const currentProjectTimeZone = currentProject?.timeZoneName;
            let projectId = null;
            let projectName = null;
            let projectType = null;
            if (currentProject && typeof currentProject === 'object') {
                const { id = null, name = null, type = null, } = currentProject;
                projectId = id;
                projectName = name;
                projectType = type;
            }
            const testOptimizeUser = getStoreValue(CONFIG.AUTH.TEST_OPTIMIZE_USER);
            const licenseTimeZone = testOptimizeUser?.responseObject?.licenseTimeZone;
            const licenseType = testOptimizeUser?.responseObject?.licenseType || '';
            if (accessToken)
                headers.set('Authorization', `Bearer ${accessToken}`);
            if (projectId && projectId !== 'All Projects') {
                headers.set('projectId', projectId);
                headers.set('projectName', projectName);
                headers.set('projectType', projectType);
            }
            if (licenseType)
                headers.set('licenseType', licenseType);
            headers.set('timeZone', projectId === 'All Projects'
                ? licenseTimeZone
                : currentProjectTimeZone);
            return headers;
        },
        timeout: CONFIG.API.TIMEOUT,
    });
    /* ---------------------------------------------
     * ♻️ Token Refresh Function
     * ------------------------------------------- */
    const refreshToken = async () => {
        if (!getStoreValue(CONFIG.AUTH.REFRESH_TOKEN_KEY)) {
            deleteStoreValue(CONFIG.AUTH.TOKEN_KEY);
            deleteStoreValue(CONFIG.AUTH.REFRESH_TOKEN_KEY);
            window.location.href = '/signin';
            return null;
        }
        if (refreshRetryCount >= MAX_REFRESH_RETRIES) {
            console.error('Max refresh attempts reached');
            deleteStoreValue(CONFIG.AUTH.TOKEN_KEY);
            deleteStoreValue(CONFIG.AUTH.REFRESH_TOKEN_KEY);
            window.location.href = '/signin';
            return null;
        }
        if (!refreshingToken) {
            refreshingToken = (async () => {
                try {
                    refreshRetryCount++;
                    const response = await fetch(`${baseUrl}/appmanagement/optimize/v1/public/user/refresh`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            refreshToken: getStoreValue(CONFIG.AUTH.REFRESH_TOKEN_KEY),
                            currentLicenseId: getStoreValue(CONFIG.AUTH.CURRENT_LICENSE_ID),
                        }),
                    });
                    if (response.ok) {
                        const data = await response.json();
                        const { access_token, refresh_token } = data;
                        setStoreValue(CONFIG.AUTH.TOKEN_KEY, access_token);
                        setStoreValue(CONFIG.AUTH.REFRESH_TOKEN_KEY, refresh_token);
                        refreshingToken = null;
                        refreshRetryCount = 0;
                        return access_token;
                    }
                    else {
                        deleteStoreValue(CONFIG.AUTH.TOKEN_KEY);
                        deleteStoreValue(CONFIG.AUTH.REFRESH_TOKEN_KEY);
                        refreshingToken = null;
                        window.location.href = '/signin';
                        return null;
                    }
                }
                catch (error) {
                    console.error('Token refresh error:', error);
                    refreshingToken = null;
                    return null;
                }
            })();
        }
        return refreshingToken;
    };
    /* ---------------------------------------------
     * ⚠️ Unauthorized Handler
     * ------------------------------------------- */
    const handleUnauthorizedError = async (args, api, extraOptions, result) => {
        const newAccessToken = await refreshToken();
        if (newAccessToken) {
            const modifiedArgs = typeof args === 'string'
                ? {
                    url: args,
                    headers: { Authorization: `Bearer ${newAccessToken}` },
                }
                : {
                    ...args,
                    headers: {
                        ...(args.headers || {}),
                        Authorization: `Bearer ${newAccessToken}`,
                    },
                };
            return await baseQuery(modifiedArgs, api, extraOptions);
        }
        else {
            deleteStoreValue(CONFIG.AUTH.TOKEN_KEY);
            deleteStoreValue(CONFIG.AUTH.REFRESH_TOKEN_KEY);
            window.location.href = '/signin';
            return result;
        }
    };
    /* ---------------------------------------------
     * 🧩 The Actual Base Query with Interceptors
     * ------------------------------------------- */
    const baseQueryWithInterceptor = async (args, api, extraOptions) => {
        let result = await baseQuery(args, api, extraOptions);
        if (result.error) {
            const error = result.error;
            const errorResponse = {
                ...error,
                message: 'An unexpected error occurred',
                statusCode: 500,
            };
            if ('status' in error) {
                errorResponse.statusCode = error.status;
                if (error.status === 401) {
                    result = await handleUnauthorizedError(args, api, extraOptions, result);
                }
                tryTriggerGlobalLock(error.status, extraOptions, error.data);
                if (typeof error.data === 'object' && error.data) {
                    errorResponse.message =
                        error.data.message ||
                            errorResponse.message;
                }
            }
            result.error = errorResponse;
        }
        else if (result?.data?.responseCode === 401) {
            result = await handleUnauthorizedError(args, api, extraOptions, result);
        }
        else {
            tryTriggerGlobalLock(result?.data?.responseCode, extraOptions, result?.data);
        }
        return result;
    };
    return baseQueryWithInterceptor;
};
/* ---------------------------------------------
 * 🧱 createCustomBaseApi Factory
 * ------------------------------------------- */
export const createCustomBaseApi = (baseUrl, reducerPath, tagTypes = []) => {
    const baseQueryWithInterceptor = createBaseQueryWithInterceptor(baseUrl);
    return createApi({
        reducerPath,
        baseQuery: baseQueryWithInterceptor,
        endpoints: () => ({}),
        tagTypes,
        keepUnusedDataFor: CONFIG.CACHE.TTL,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: false,
        refetchOnReconnect: true,
    });
};
//# sourceMappingURL=baseApi.js.map